import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, ArrowLeft, Eye, EyeOff, AlertCircle } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/ui/Button';
import livePulseLogo from '../assets/live-pulse-logo.png';
import { API_PATH, request } from '../config/api';
import PasswordStrengthMeter from '../components/ui/PasswordStrengthMeter';
import styles from './ForgotPassword.module.css';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!token) {
            setError('Invalid or missing reset link. Please request a new one from the forgot password page.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setIsLoading(true);
        try {
            const res = await request(`${API_PATH}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'Failed to reset password. The link may have expired.');
                return;
            }
            setSuccess(true);
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!token && !success) {
        return (
            <div className={styles.page}>
                <div className={styles.themeBtn}><ThemeToggle /></div>
                <div className={styles.card}>
                    <Link to="/" className={styles.logoLink}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.logoName} />
                    </Link>
                    <div className={styles.formSection}>
                        <h1 className={styles.title}>Invalid reset link</h1>
                        <p className={styles.subtitle}>
                            This link is missing or invalid. Please request a new password reset from the login page.
                        </p>
                        <Link to="/forgot-password" className={styles.backLink}>
                            <ArrowLeft size={15} /> Forgot password
                        </Link>
                        <Link to="/login" className={`${styles.successBtn} btn-pill-primary`} style={{ marginTop: 12 }}>
                            Back to login <ArrowRight size={17} />
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className={styles.page}>
                <div className={styles.themeBtn}><ThemeToggle /></div>
                <div className={styles.card}>
                    <Link to="/" className={styles.logoLink}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.logoName} />
                    </Link>
                    <div className={styles.successSection}>
                        <div className={styles.successIconWrap}>
                            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                                <circle cx="28" cy="28" r="28" fill="#10b981" fillOpacity="0.12" />
                                <path d="M18 28l7 7 13-13" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h1 className={styles.title}>Password reset</h1>
                        <p className={styles.subtitle}>
                            Your password has been updated. You can now sign in with your new password.
                        </p>
                        <Link to="/login" className={`${styles.successBtn} btn-pill-primary`} onClick={() => navigate('/login')}>
                            Sign in <ArrowRight size={17} />
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.themeBtn}><ThemeToggle /></div>
            <div className={styles.card}>
                <Link to="/" className={styles.logoLink}>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.logoName} />
                </Link>
                <div className={styles.formSection}>
                    <div className={styles.iconWrap}>
                        <Lock size={28} className={styles.headerIcon} />
                    </div>
                    <h1 className={styles.title}>Set new password</h1>
                    <p className={styles.subtitle}>
                        Enter your new password below. Use at least 8 characters.
                    </p>

                    {error && (
                        <div className={styles.errorBanner} role="alert">
                            <AlertCircle size={15} aria-hidden="true" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label htmlFor="password" className={styles.label}>New password</label>
                            <div className={styles.inputWrapper}>
                                <Lock size={17} className={styles.inputIcon} />
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Min. 8 characters"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={styles.input}
                                    required
                                    minLength={8}
                                    disabled={isLoading}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className={styles.eyeBtn}
                                    onClick={() => setShowPassword((v) => !v)}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            <PasswordStrengthMeter password={password} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="confirmPassword" className={styles.label}>Confirm password</label>
                            <div className={styles.inputWrapper}>
                                <Lock size={17} className={styles.inputIcon} />
                                <input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Re-enter password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={styles.input}
                                    required
                                    minLength={8}
                                    disabled={isLoading}
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            className={`${styles.submitBtn} btn-pill-primary`}
                            disabled={isLoading}
                            loading={isLoading}
                            iconRight={!isLoading ? <ArrowRight size={17} /> : null}
                        >
                            {isLoading ? 'Updating...' : 'Reset password'}
                        </Button>
                    </form>

                    <Link to="/login" className={styles.backLink}>
                        <ArrowLeft size={15} /> Back to login
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
